/* EMRKT.CH — client-side i18n dictionary (PT/EN/DE/FR/IT).
 * Kept flat and small; missing keys fall back to English. */

const DICT = {
  en: {
    brand: "EMRKT.CH",
    tagline: "The Future of Autonomous Commerce",
    nav_login: "Log in",
    nav_get_started: "Get started",
    hero_kicker: "Phase 1 — Foundation",
    hero_title: "Autonomous commerce.\nRun from Switzerland.",
    hero_body: "EMRKT.CH orchestrates twelve AI modules that discover opportunities, list products, price them dynamically, serve customers in five languages, and keep every decision compliant, logged, and explainable.",
    hero_cta_primary: "Open the Dashboard",
    hero_cta_secondary: "Explore the modules",
    mission_kicker: "Mission",
    mission_title: "Automate the entire commerce lifecycle. Keep humans in charge of the strategy.",
    vision_kicker: "Vision",
    vision_title: "A trust-first, self-improving commerce operator that converges toward CHF 1,000 net profit per day.",
    principles_title: "Operating principles",
    p1_title: "Trust before profit",
    p1_body: "Customer trust is treated as the compound asset, not the marketing headline.",
    p2_title: "Legal by design",
    p2_body: "Every decision runs against local law and platform policy before it ships.",
    p3_title: "Auditable & explainable",
    p3_body: "Uncertain, legal or strategic decisions are escalated with a full trace.",
    p4_title: "Sustainable margin",
    p4_body: "The Finance module reserves tax obligations before calling anything profit.",
    modules_kicker: "Twelve autonomous modules",
    modules_title: "One CEO AI. Eleven specialists. Zero drift.",
    modules_body: "The CEO AI orchestrates decisions across trend detection, pricing, SEO, marketing, customer support, finance and compliance.",
    roadmap_title: "Roadmap",
    roadmap_p1: "Phase 1 — Foundation (infrastructure, auth, database, CI/CD, tests)",
    roadmap_p2: "Phase 2 — Commerce core (catalog, checkout, orders, suppliers)",
    roadmap_p3: "Phase 3 — Automation (listing, translation, support, sync)",
    roadmap_p4: "Phase 4 — Intelligence (trend, pricing, SEO, campaigns)",
    roadmap_p5: "Phase 5 — Continuous learning (experiments, self-improvement)",
    footer_note: "EMRKT.CH · Built for long-term trust, not short-term hype.",

    login_title: "Sign in",
    login_subtitle: "Access the EMRKT.CH operator console.",
    login_email: "Email",
    login_password: "Password",
    login_submit: "Sign in",
    login_google: "Continue with Google",
    login_no_account: "No account?",
    login_register: "Create one",
    register_title: "Create account",
    register_name: "Full name",
    register_submit: "Create account",
    register_have_account: "Already have an account?",
    logging_in: "Signing in…",
    creating: "Creating…",

    sidebar_overview: "Overview",
    sidebar_modules: "AI Modules",
    sidebar_guardian: "Guardian AI",
    sidebar_growth: "Growth Studio",
    sidebar_ebooks: "Ebooks",
    sidebar_finance: "Finance",
    sidebar_audit: "Audit Log",
    sidebar_settings: "Settings",
    sidebar_logout: "Log out",

    overview_title: "Operator console",
    overview_kpi_revenue: "Revenue today",
    overview_kpi_profit: "Net profit today",
    overview_kpi_orders: "Orders today",
    overview_kpi_trust: "Trust score",
    overview_kpi_autonomy: "Support autonomy",
    wd_title: "14-day withdrawal",
    wd_intro: "You may withdraw from any order within 14 days without giving a reason. This is your legal right under Swiss and EU consumer law. One click below is enough — no chat required, no forms to chase.",
    wd_order: "Order or session number",
    wd_email: "Email used at checkout",
    wd_reason: "Reason (optional)",
    wd_reason_ph: "e.g. changed my mind, arrived too late…",
    wd_submit: "Request withdrawal",
    wd_sending: "Sending…",
    wd_footer: "Equipa EMRKT.CH confirms within 24h. Orders ≤ CHF 15 are auto-refunded without needing to ship the product back.",
    wd_auto: "This order qualifies for an automatic refund — no need to return the product.",
    wd_button_from_order: "Request 14-day withdrawal",
    wd_14day_reminder: "14 days · one click · no chat required",

    module_howto_title: "What is this and how to use it",
    howto_ceo: "AI Coordinator — What: your morning briefing. It reads everything the other modules found and tells you what needs your attention today. How: click 'Refresh briefing' or ask a specific question (e.g. 'what should I ship this week?').",
    howto_trend: "Discover trends — What: finds products that are starting to grow so you sell them before everyone else. How: type a keyword (e.g. 'cat accessories', 'kitchen gadgets') and click Analyse. Save the interesting ones for later.",
    howto_market: "Market size — What: tells you how big the opportunity is and how many competitors already exist. How: type the product or category and click Analyse. Use it before you commit to a niche.",
    howto_product: "Products — What: turns a product idea into a full listing (title, description in 5 languages, tags, SEO) ready to sell. How: pick an existing product and click Run to auto-fill its listing, or type a new idea in 'Create products' and click Generate. Then approve and Push to Shopify.",
    howto_supplier: "Suppliers & stock — What: shows how many units are available with your supplier (CJ Dropshipping / DSers) in real time. How: check the list; adjust 'Threshold' to change when a product is flagged as low stock.",
    howto_pricing: "Prices — What: suggests the right price for each product to be attractive AND profitable. How: click Run on any product to see the suggested price and the % difference. For international sales, use 'Compute per-country pricing' (Switzerland, Germany, Austria, France, Italy).",
    howto_seo: "Google visibility — What: writes the product texts that show up on Google. How: type the product's URL slug (like 'rotating-spice-rack') and click Analyse to get an optimised title + description.",
    howto_marketing: "Marketing & campaigns — What: builds your brand look and plans ads. How: pick the goal (Get known / Sell more / Bring back buyers) and click Analyse for a campaign plan. For the shop visuals, type a short brand description and click 'Generate design brief' — you get colours, fonts and hero text ready to apply.",
    howto_customer: "Customers & support — What: the automatic support team that answers questions on your shop. How: 'Customer conversations' shows what buyers asked, with an ESCALATED badge on cases that need you. To add real reviews, pick the product, paste them as JSON or upload a CSV, and click Import.",
    howto_finance: "Money & profit — What: the honest 'how much did I really make' view — sales minus costs, taxes and returns reserved. How: check the numbers before taking profit out; refresh anytime you want reality.",
    howto_guardian: "Safety & legal — What: makes sure the shop is legal and every decision has a trace. How: open 'Legal & Trust pages', regenerate the text if needed, Approve for your language, then Publish to Shopify. Check 'Launch Readiness' to see products that stopped passing the safety rules.",
    howto_learning: "Auto-improve — What: watches what sells and proposes smart changes every week (raise a price here, pause a bad product there). How: no action needed most of the time; when you see 'proposals pending', click Apply to accept or A/B test to split-test the change safely.",
    audience_title: "Target customer",
    audience_persona: "Primary persona",
    audience_persona_body: "Women 25-55 in Switzerland + EU. Practical, aesthetic, trust-first buyers.",
    audience_insufficient: "Not enough orders yet to compute reliable statistics — we don't invent data.",
    audience_col_metric: "Metric",
    audience_col_value: "Value",

    overview_target: "Target · CHF 1,000/day",
    overview_modules_active: "Modules active",
    overview_guardian: "Guardian status",
    overview_briefing: "CEO AI briefing",
    overview_briefing_hint: "Generated by GPT-5.2 in your language.",
    overview_run_briefing: "Refresh briefing",

    guardian_title: "Guardian AI — System health",
    guardian_subtitle: "Continuous integrity of infrastructure, integrations and compliance.",
    guardian_component: "Component",
    guardian_status: "Status",
    guardian_latency: "Latency",
    guardian_message: "Message",

    audit_title: "Audit log",
    audit_subtitle: "Every critical decision leaves a trace.",
    audit_ts: "Timestamp",
    audit_action: "Action",
    audit_actor: "Actor",
    audit_meta: "Meta",

    finance_title: "Finance overview",
    finance_subtitle: "Revenue, costs, tax reserve, distributable profit.",
    finance_revenue: "Revenue",
    finance_costs: "Costs",
    finance_gross: "Gross profit",
    finance_tax_reserve: "Tax reserve (25%)",
    finance_distributable: "Distributable profit",
    finance_target: "Daily target",

    module_status_active: "active",
    module_status_standby: "standby",
    module_status_monitoring: "monitoring",
    module_status_training: "training",
    module_briefing_title: "AI briefing",
    module_briefing_generate: "Generate briefing",
    module_briefing_generating: "Generating…",
    module_briefing_ask: "Ask this module",
    module_briefing_placeholder: "Ask a question or leave blank for a default briefing…",

    settings_title: "Settings",
    settings_language: "Interface language",
    settings_profile: "Profile",

    sidebar_shopify: "Shopify",
    cp_required: "Security requirement",
    cp_title: "Set a new password",
    cp_hint: "For your first sign-in you must replace the seeded password with one only you know. This screen cannot be skipped.",
    cp_current: "Current password",
    cp_new: "New password",
    cp_confirm: "Confirm new password",
    cp_submit: "Save & continue",
    cp_saving: "Saving…",
    cp_success: "Password updated",
    cp_failed: "Could not update password",
    cp_min_length: "New password must be at least 8 characters",
    cp_mismatch: "Passwords do not match",

    shopify_title: "Shopify — Product sync",
    shopify_subtitle: "Read-only import from your Shopify store into EMRKT.CH.",
    shopify_status: "Status",
    shopify_shop: "Shop",
    shopify_currency: "Currency",
    shopify_last_sync: "Last sync",
    shopify_connected: "connected",
    shopify_not_configured: "not configured",
    shopify_error: "error",
    shopify_setup_needed: "Setup required",
    shopify_setup_instructions: "Paste the SHOPIFY_ADMIN_TOKEN (shpat_...) into the Emergent secret manager or the backend .env, then restart the backend and click Refresh.",
    shopify_sync_now: "Sync now",
    shopify_syncing: "Syncing…",
    shopify_synced: "products fetched",
    shopify_sync_failed: "Sync failed",
    shopify_products: "Products",
    shopify_search_placeholder: "Search by title, handle, vendor…",
    shopify_empty: "No products synced yet — click Sync now.",
    shopify_not_connected: "Shopify not connected — click Connect Shopify above.",
    shopify_connect: "Connect Shopify",
    shopify_disconnect: "Disconnect",
    shopify_connected_toast: "Shopify connected",
    shopify_disconnected_toast: "Shopify disconnected",
    shopify_disconnected: "not connected",
    shopify_connect_hint_title: "One click to connect",
    shopify_connect_hint_body: "Clicking Connect will send you to Shopify to approve the app. After approval you'll return here and can sync products.",
    shopify_col_title: "Title",
    shopify_col_vendor: "Vendor",
    shopify_col_type: "Type",
    shopify_col_status: "Status",
    shopify_col_variants: "Variants",
    shopify_col_updated: "Updated",
    shopify_new_tab_opened: "Shopify approval opened in a new tab. Complete it there, then come back.",

    shop_kicker: "The catalog",
    shop_title: "Objects that make a house feel like home.",
    shop_subtitle: "A quiet, tightly curated selection of decor, living and lifestyle pieces — hand-picked in Switzerland, shipped worldwide, priced in CHF.",
    shop_catalog_title: "The Edit",
    shop_pieces_curated: "pieces curated",
    shop_cat_all: "All",
    shop_trust_secure: "Secure checkout",
    shop_trust_shipping: "Worldwide shipping",
    shop_trust_curated: "Hand curated",
    shop_view: "view",
    shop_loading: "loading products…",
    shop_empty: "No products yet — check back soon.",
    shop_view: "view",
    shop_back: "Back to catalog",
    shop_not_found: "Product not found.",
    shop_ai_enriched: "AI-enriched",
    shop_buy_now: "Buy now",
    shop_processing: "Redirecting…",
    shop_secure_checkout: "Secure checkout via Stripe · CHF",
    shop_checkout_failed: "Checkout could not start",
    shop_checkout_cancelled: "Checkout cancelled",
    shop_success_title: "Order confirmed",
    shop_success_body: "Thank you — your payment was received. A confirmation email is on its way.",
    shop_success_pending: "Almost there",
    shop_success_pending_body: "Your payment is being processed. You can safely close this tab.",
    shop_back_to_catalog: "Back to catalog",
    referral_prompt_title: "Share & earn CHF 3 on your next order",
    referral_prompt_body: "Send your personal link to a friend. Every time someone buys through it, we credit CHF 3 to your account towards your next order at EMRKT.CH.",
    referral_prompt_email_placeholder: "Your email",
    referral_prompt_generate: "Get my link",
    referral_prompt_copy: "Copy link",
    referral_prompt_copied: "Link copied — share away!",
    referral_prompt_share: "Share on WhatsApp",

    growth_title: "Growth Studio",
    growth_subtitle: "Ready-to-post captions for Instagram, TikTok, Pinterest, WhatsApp, Facebook, X, LinkedIn and Reddit — in 5 languages. Copy, paste to your own social accounts, drive free organic traffic to your shop.",
    growth_bulk_generate: "Generate for all products",
    growth_generating: "Generating…",
    growth_generate_one: "Generate this kit",
    growth_regenerate: "Regenerate",
    growth_products: "Products",
    growth_no_products: "Sync Shopify first to see products.",
    growth_pick_product: "Pick a product on the left.",
    growth_not_generated: "No kit yet — click Generate to write your first batch of posts.",
    growth_no_content: "No content for this channel yet — regenerate to fill it in.",
    growth_copy: "Copy",
    growth_copied: "Copied to clipboard — paste to your social account.",
    growth_kit_ready: "Kit ready. Pick a channel and copy.",
    growth_tip: "Tip · Paste to your account, add the product image (right-click 'copy image' on the product page), publish.",

    newsletter_title: "Get CHF deals in your inbox",
    newsletter_body: "One email per week with new arrivals and Swiss-friendly deals. No spam, unsubscribe with one click.",
    newsletter_email_placeholder: "your@email.ch",
    newsletter_subscribe: "Subscribe",
    newsletter_subscribed_toast: "Subscribed — thank you!",
    newsletter_subscribed_body: "You're on the list — see you in your inbox soon.",
    newsletter_disclaimer: "GDPR / Swiss LCD compliant · one-click unsubscribe",

    ebooks_title: "Digital guides",
    ebooks_subtitle: "Compact, honest, expert-referenced guides for the moments that don't fit into a Google search. Delivered as PDF by email in seconds.",
    ebooks_available: "available",
    ebooks_none_yet: "New guides are being written. Come back soon.",
    ebooks_all: "All guides",
    ebooks_email_placeholder: "your@email.ch",
    ebooks_buy_now: "Buy & download",
    ebooks_digital_disclaimer: "Digital product · immediate delivery · consumer withdrawal right waived on first download (Swiss LCD).",
    ebooks_success_title: "Payment received",
    ebooks_success_body: "Your PDF is ready and a copy is on its way to your inbox.",
    ebooks_success_pending: "Almost there",
    ebooks_success_pending_body: "Confirming your payment — this usually takes a few seconds.",
    ebooks_download_now: "Download PDF",
    ebooks_email_sent_to: "Also sent to",
    ebooks_browse_more: "Browse more guides",
    shop_ebooks_link: "Digital guides",

    ebooks_admin_title: "Ebooks",
    ebooks_admin_subtitle: "GPT-5.2 ghost-writes 4 000–5 500-word guides on sensitive parenting topics with attributed expert references. 100% margin.",
    ebooks_seed_title: "One-click generation",
    ebooks_seed_body: "Sensitive-topic playbook — click to start writing a new guide. Each takes ~2 minutes.",
    ebooks_generate: "Generate",
    ebooks_admin_library: "Library",
    ebooks_admin_empty: "No ebooks yet. Click a seed topic above to write the first one.",

    intel_product_title: "Product listings — auto-generated",
    intel_pricing_title: "Suggested prices",
    intel_run: "Run",
    intel_redo: "Re-run",
    intel_run_all: "Run for all",
    intel_running_all: "Running…",
    intel_enriched: "Product enriched",
    intel_priced: "Price recommended",
    intel_no_products: "Sync Shopify first to see products here.",
    intel_col_product: "Product",
    intel_col_langs: "Languages",
    intel_col_tags: "Tags",
    intel_col_current: "Current",
    intel_col_recommended: "Recommended",
    intel_col_delta: "Δ",
    intel_col_confidence: "Confidence",

    auto_title: "Create new products",
    auto_subtitle: "Describe an idea or trend. We build the whole product — title, 5-language description, tags and price — ready for you to approve.",
    auto_seed_placeholder: "e.g. minimalist stainless steel travel mugs under CHF 40",
    auto_generate: "Generate draft",
    auto_generating: "Generating…",
    auto_generated_ok: "Draft created",
    auto_published_ok: "Draft pushed to Shopify (status: draft)",
    auto_col_title: "Title",
    auto_col_type: "Type",
    auto_col_price: "Price",
    auto_col_status: "Status",
    auto_publish: "Push to Shopify",
    auto_reject: "Reject",
    auto_view_shopify: "Open in Shopify",
    auto_empty: "No AI-generated drafts yet.",
    auto_status_draft: "draft",
    auto_status_published: "published",
    auto_status_rejected: "rejected",

    support_title: "Customer conversations",
    support_only_escalated: "Escalated only",
    support_escalated: "escalated",
    support_col_customer: "Customer",
    support_col_last: "Last message",
    support_col_msgs: "Msgs",
    support_col_when: "Updated",
    support_anonymous: "anonymous",
    support_empty: "No conversations yet.",
    support_resolve: "Resolve",
    support_resolved: "Marked as resolved",
    support_status_resolved: "resolved",

    inv_title: "Inventory snapshot",
    inv_low_stock: "low-stock items",
    inv_threshold: "Threshold",
    inv_col_product: "Product",
    inv_col_stock: "Stock",
    inv_col_variants: "Variants",
    inv_empty: "No inventory data — sync Shopify first.",

    chat_title: "EMRKT.CH Support",
    chat_open: "Open chat",
    chat_send: "Send",
    chat_placeholder: "Ask about a product, shipping, returns…",
    chat_thinking: "typing…",
    chat_welcome: "Hi! I'm the EMRKT.CH assistant. I can help you with products, shipping and returns. What can I do for you?",
    chat_error: "Sorry, I couldn't reach the server. Please try again.",
    chat_escalated: "Forwarded to a human specialist",

    p4_trend_title: "Discover trends",
    p4_market_title: "Market size",
    p4_seo_title: "Google visibility",
    p4_mkt_title: "Marketing & campaigns",
    p4_trend_ph: "e.g. sustainable pet accessories",
    p4_market_ph: "e.g. smart pet feeders",
    p4_seo_ph: "product handle from your Shopify (e.g. rotating-spice-rack)",
    p4_mkt_ph: "e.g. Rotating Spice Rack",
    p4_run: "Analyse",
    p4_running: "Analysing…",
    p4_done: "Analysis ready",
    p4_history: "Recent analyses",
    p4_obj_awareness: "Awareness",
    p4_obj_conversion: "Conversion",
    p4_obj_retention: "Retention",
    p4_pricing_country_title: "Per-country pricing (CH/DE/AT/FR/IT)",
    p4_pricing_compute: "Compute",
    p4_pricing_recompute: "Recompute",
    p4_pricing_done: "Country prices computed",

    login_admin_only: "Platform locked · admin only during development",
    coming_soon_title: "Under development",
    coming_soon_body: "This platform is not yet open to the public. It is currently in a private development phase — come back soon.",
    coming_soon_home: "Back to home",

    reviews_title: "Customer reviews",
    reviews_count: "reviews",
    reviews_verified: "verified purchase",
    reviews_verified_purchase: "verified",
    reviews_more: "Read more",
    reviews_less: "Show less",
    reviews_admin_title: "Product reviews (real, imported)",
    reviews_admin_disclaimer: "Real reviews only · Swiss transparency law",
    reviews_product: "Product",
    reviews_paste_json: "Paste reviews as JSON",
    reviews_import_json: "Import",
    reviews_import_csv: "Upload CSV",
    reviews_imported: "reviews imported",
    reviews_col_author: "Author",
    reviews_col_rating: "Rating",
    reviews_col_body: "Body",
    reviews_col_source: "Source",
    reviews_empty: "No reviews yet — import from AliExpress or CSV.",
    reviews_judgeme_sync: "Sync Judge.me",
    reviews_judgeme_syncing: "Syncing…",
    reviews_judgeme_synced: "reviews synced from Judge.me",
    reviews_judgeme_not_configured: "Judge.me not configured on the backend",

    legal_title: "Legal & Trust pages",
    legal_status_empty: "Not created",
    legal_status_draft: "Draft",
    legal_status_approved: "Approved",
    legal_status_published: "Published",
    legal_generate: "Generate",
    legal_regenerate: "Regenerate",
    legal_approve: "Approve current language",
    legal_generated: "Draft generated",
    legal_approved: "Approved and now public",
    legal_publish_shopify: "Publish to Shopify",
    legal_view_shopify: "View on Shopify",
    legal_published_shopify: "Published to Shopify",
    legal_edit_title: "Title",
    legal_edit_body: "Body (Markdown)",
    legal_status_published: "published",
    legal_last_updated: "Last updated",
    legal_none: "Not generated yet.",
    legal_not_published: "This page has not been published yet.",
    legal_pt_terms: "Terms of Sale",
    legal_pt_privacy: "Privacy",
    legal_pt_dropshipping_disclosure: "Dropshipping Disclosure",
    legal_pt_refund: "Refund & Withdrawal",
    legal_pt_shipping: "Shipping",
    legal_pt_imprint: "Imprint",

    footer_transparency_title: "Full transparency",
    footer_transparency_body: "EMRKT.CH is a Swiss automated dropshipping platform. Products are shipped directly by our third-party suppliers. Shipping cost and product responsibility belong to the supplier — we operate the curation, payment and support layer between you and the supplier. Your consumer rights (14-day withdrawal, warranty) remain fully protected.",
    footer_read_full: "Read the full disclosure",

    designer_title: "Shop look & brand",
    designer_context: "Brand context (optional)",
    designer_context_ph: "e.g. Operated from Switzerland with a warm Mediterranean voice. Buyers are 30-45, urban, design-aware.",
    designer_generate: "Generate brief",
    designer_generating: "Designing…",
    designer_generated: "Design brief ready",
    designer_briefs: "Recent briefs",
    designer_activate: "Activate",
    designer_activated: "Brief activated — storefront updated",
    designer_active: "active",
    designer_view: "View",
    designer_none: "Generate your first brand brief above.",
  },
  pt: {
    brand: "EMRKT.CH",
    tagline: "O Futuro do Comércio Autónomo",
    nav_login: "Entrar",
    nav_get_started: "Começar",
    hero_kicker: "Fase 1 — Fundação",
    hero_title: "Comércio autónomo.\nOperado da Suíça.",
    hero_body: "A EMRKT.CH orquestra doze módulos de IA que descobrem oportunidades, publicam produtos, definem preços dinamicamente, servem clientes em cinco idiomas e mantêm todas as decisões conformes, registadas e explicáveis.",
    hero_cta_primary: "Abrir o Dashboard",
    hero_cta_secondary: "Explorar os módulos",
    mission_kicker: "Missão",
    mission_title: "Automatizar todo o ciclo de comércio. Manter os humanos no controlo da estratégia.",
    vision_kicker: "Visão",
    vision_title: "Um operador de comércio que se auto-melhora, orientado por confiança, com meta de CHF 1.000 de lucro líquido por dia.",
    principles_title: "Princípios operacionais",
    p1_title: "Confiança antes do lucro",
    p1_body: "A confiança do cliente é o activo composto, não o slogan de marketing.",
    p2_title: "Legalidade por design",
    p2_body: "Cada decisão é validada contra a lei local e políticas de plataforma antes de sair.",
    p3_title: "Auditável e explicável",
    p3_body: "Decisões incertas, legais ou estratégicas são escaladas com rasto completo.",
    p4_title: "Margem sustentável",
    p4_body: "O módulo Finance reserva obrigações fiscais antes de chamar algo lucro.",
    modules_kicker: "Doze módulos autónomos",
    modules_title: "Uma CEO AI. Onze especialistas. Zero drift.",
    modules_body: "A CEO AI coordena decisões em tendências, preços, SEO, marketing, apoio ao cliente, finanças e compliance.",
    roadmap_title: "Roadmap",
    roadmap_p1: "Fase 1 — Fundação (infraestrutura, autenticação, base de dados, CI/CD, testes)",
    roadmap_p2: "Fase 2 — Núcleo de Comércio (catálogo, checkout, encomendas, fornecedores)",
    roadmap_p3: "Fase 3 — Automação (listagens, traduções, apoio, sincronização)",
    roadmap_p4: "Fase 4 — Inteligência (tendências, preços, SEO, campanhas)",
    roadmap_p5: "Fase 5 — Aprendizagem contínua (experimentação, auto-melhoria)",
    footer_note: "EMRKT.CH · Construído para confiança de longo prazo, não hype de curto prazo.",
    login_title: "Entrar",
    login_subtitle: "Aceder à consola de operador EMRKT.CH.",
    login_email: "Email",
    login_password: "Palavra-passe",
    login_submit: "Entrar",
    login_google: "Continuar com Google",
    login_no_account: "Sem conta?",
    login_register: "Criar uma",
    register_title: "Criar conta",
    register_name: "Nome completo",
    register_submit: "Criar conta",
    register_have_account: "Já tem conta?",
    logging_in: "A entrar…",
    creating: "A criar…",
    sidebar_overview: "Visão Geral",
    sidebar_modules: "Módulos IA",
    sidebar_guardian: "Guardian AI",
    sidebar_growth: "Studio de crescimento",
    sidebar_finance: "Finanças",
    sidebar_audit: "Log de Auditoria",
    sidebar_settings: "Definições",
    sidebar_logout: "Sair",
    overview_title: "Consola do operador",
    overview_kpi_revenue: "Receita hoje",
    overview_kpi_profit: "Lucro líquido hoje",
    overview_kpi_orders: "Encomendas hoje",
    overview_kpi_trust: "Índice de confiança",
    overview_kpi_autonomy: "Autonomia do apoio",
    module_howto_title: "O que é isto e como uso",
    howto_ceo: "Coordenador IA — O que é: o teu briefing da manhã. Lê tudo o que os outros módulos descobriram e diz-te o que precisa da tua atenção hoje. Como usar: clica 'Atualizar briefing', ou faz uma pergunta específica (ex: 'o que devo enviar esta semana?').",
    howto_trend: "Descobrir tendências — O que é: encontra produtos que estão a começar a crescer, para os venderes antes dos outros. Como usar: escreve uma palavra-chave (ex: 'acessórios para gatos', 'utensílios cozinha') e clica Analisar. Guarda os interessantes para depois.",
    howto_market: "Tamanho do mercado — O que é: diz-te quão grande é a oportunidade e quantos concorrentes já existem. Como usar: escreve o produto/categoria e clica Analisar. Usa antes de te comprometeres com um nicho.",
    howto_product: "Produtos — O que é: transforma uma ideia num anúncio completo (título, descrição em 5 idiomas, tags, SEO) pronto a vender. Como usar: escolhe um produto e clica Correr para preencher automaticamente, ou escreve uma ideia nova em 'Criar produtos' e clica Gerar. Depois aprova e Enviar para Shopify.",
    howto_supplier: "Fornecedores e stock — O que é: mostra em tempo real quantas unidades tens disponíveis no fornecedor (CJ Dropshipping / DSers). Como usar: consulta a lista; ajusta 'Limite' para mudar a partir de quantas unidades um produto é assinalado como pouco stock.",
    howto_pricing: "Preços — O que é: sugere o preço certo para cada produto ser atrativo E lucrativo. Como usar: clica Correr num produto para veres o preço sugerido e a diferença %. Para vender internacionalmente, usa 'Calcular preços por país' (Suíça, Alemanha, Áustria, França, Itália).",
    howto_seo: "Visibilidade no Google — O que é: escreve os textos dos produtos para aparecerem no Google. Como usar: escreve o slug URL do produto (ex: 'rotating-spice-rack') e clica Analisar para obteres título + descrição otimizados.",
    howto_marketing: "Marketing e campanhas — O que é: cria a imagem da tua marca e planeia anúncios. Como usar: escolhe o objetivo (Dar-me a conhecer / Vender mais / Recuperar clientes) e clica Analisar para o plano da campanha. Para o visual da loja, escreve uma breve descrição da marca e clica 'Gerar brief de design' — recebes cores, tipografia e texto principal prontos a aplicar.",
    howto_customer: "Clientes e apoio — O que é: a equipa de apoio automática que responde às perguntas na tua loja. Como usar: 'Conversas com clientes' mostra o que perguntaram, com aviso ESCALADA nos casos que precisam de ti. Para adicionar reviews reais, escolhe o produto, cola em JSON ou faz upload de CSV e clica Importar.",
    howto_finance: "Dinheiro e lucro — O que é: a vista honesta do 'quanto ganhei mesmo' — vendas menos custos, impostos e reservas para devoluções. Como usar: consulta os números antes de levantares lucro; atualiza sempre que quiseres a realidade.",
    howto_guardian: "Segurança e legal — O que é: garante que a loja é legal e cada decisão fica registada. Como usar: em 'Páginas Legais', regenera o texto se precisares, Aprova para o teu idioma, depois Publica no Shopify. Consulta 'Launch Readiness' para veres produtos que deixaram de cumprir as regras de segurança.",
    howto_learning: "Auto-melhoria — O que é: observa o que vende e propõe mudanças inteligentes todas as semanas (subir um preço aqui, pausar um produto mau ali). Como usar: quase sempre não é preciso ação; quando vires 'propostas pendentes', clica Aplicar para aceitar ou Teste A/B para testar a mudança em segurança.",
    audience_title: "Cliente Alvo",
    audience_persona: "Persona principal",
    audience_persona_body: "Mulheres 25-55 anos na Suíça + UE. Práticas, com sensibilidade estética, decidem pela confiança.",
    audience_insufficient: "Ainda sem encomendas suficientes para estatística fiável — não inventamos dados.",
    audience_col_metric: "Métrica",
    audience_col_value: "Valor",
    wd_title: "Retratação em 14 dias",
    wd_intro: "Podes retratar qualquer encomenda em 14 dias sem indicar motivo. É o teu direito legal ao abrigo da lei suíça e da UE. Um clique aqui é suficiente — sem chat obrigatório, sem formulários longos.",
    wd_order: "Número da encomenda ou sessão",
    wd_email: "Email usado no checkout",
    wd_reason: "Motivo (opcional)",
    wd_reason_ph: "ex: mudei de ideias, chegou tarde…",
    wd_submit: "Pedir retratação",
    wd_sending: "A enviar…",
    wd_footer: "A Equipa EMRKT.CH confirma em 24h. Encomendas ≤ CHF 15 são reembolsadas automaticamente sem precisares de devolver o produto.",
    wd_auto: "Esta encomenda qualifica para reembolso automático — não é preciso devolver o produto.",
    wd_button_from_order: "Pedir retratação (14 dias)",
    wd_14day_reminder: "14 dias · um clique · sem chat obrigatório",


    overview_target: "Meta · CHF 1.000/dia",
    overview_modules_active: "Módulos activos",
    overview_guardian: "Estado Guardian",
    overview_briefing: "Briefing da CEO AI",
    overview_briefing_hint: "Gerado por GPT-5.2 no teu idioma.",
    overview_run_briefing: "Actualizar briefing",
    guardian_title: "Guardian AI — Saúde do sistema",
    guardian_subtitle: "Integridade contínua de infraestrutura, integrações e compliance.",
    guardian_component: "Componente",
    guardian_status: "Estado",
    guardian_latency: "Latência",
    guardian_message: "Mensagem",
    audit_title: "Log de auditoria",
    audit_subtitle: "Cada decisão crítica deixa rasto.",
    audit_ts: "Data/Hora",
    audit_action: "Acção",
    audit_actor: "Actor",
    audit_meta: "Meta",
    finance_title: "Visão financeira",
    finance_subtitle: "Receita, custos, reserva fiscal, lucro distribuível.",
    finance_revenue: "Receita",
    finance_costs: "Custos",
    finance_gross: "Lucro bruto",
    finance_tax_reserve: "Reserva fiscal (25%)",
    finance_distributable: "Lucro distribuível",
    finance_target: "Meta diária",
    module_status_active: "activo",
    module_status_standby: "standby",
    module_status_monitoring: "monitor",
    module_status_training: "treino",
    module_briefing_title: "Briefing IA",
    module_briefing_generate: "Gerar briefing",
    module_briefing_generating: "A gerar…",
    module_briefing_ask: "Perguntar a este módulo",
    module_briefing_placeholder: "Faz uma pergunta ou deixa em branco para briefing padrão…",
    settings_title: "Definições",
    settings_language: "Idioma da interface",
    settings_profile: "Perfil",

    sidebar_shopify: "Shopify",
    cp_required: "Requisito de segurança",
    cp_title: "Define uma nova password",
    cp_hint: "No primeiro login tens de substituir a password inicial por uma que só tu conheces. Este ecrã não pode ser saltado.",
    cp_current: "Password actual",
    cp_new: "Nova password",
    cp_confirm: "Confirma a nova password",
    cp_submit: "Guardar e continuar",
    cp_saving: "A guardar…",
    cp_success: "Password actualizada",
    cp_failed: "Não foi possível actualizar",
    cp_min_length: "A nova password precisa de pelo menos 8 caracteres",
    cp_mismatch: "As passwords não coincidem",

    shopify_title: "Shopify — Sincronização de produtos",
    shopify_subtitle: "Importação read-only da tua loja Shopify para o EMRKT.CH.",
    shopify_status: "Estado",
    shopify_shop: "Loja",
    shopify_currency: "Moeda",
    shopify_last_sync: "Última sincronização",
    shopify_connected: "ligado",
    shopify_not_configured: "não configurado",
    shopify_error: "erro",
    shopify_setup_needed: "Configuração necessária",
    shopify_setup_instructions: "Cola o SHOPIFY_ADMIN_TOKEN (shpat_...) no secret manager do Emergent ou no backend .env, reinicia o backend e clica em Actualizar.",
    shopify_sync_now: "Sincronizar agora",
    shopify_syncing: "A sincronizar…",
    shopify_synced: "produtos importados",
    shopify_sync_failed: "Sincronização falhou",
    shopify_products: "Produtos",
    shopify_search_placeholder: "Pesquisar por título, handle, fornecedor…",
    shopify_empty: "Ainda sem produtos — clica em Sincronizar agora.",
    shopify_not_connected: "Shopify não ligada — clica em Ligar Shopify acima.",
    shopify_connect: "Ligar Shopify",
    shopify_disconnect: "Desligar",
    shopify_connected_toast: "Shopify ligada",
    shopify_disconnected_toast: "Shopify desligada",
    shopify_disconnected: "não ligada",
    shopify_connect_hint_title: "Um clique para ligar",
    shopify_connect_hint_body: "Ao clicar Ligar, és enviada para a Shopify para aprovar a app. Depois voltas aqui e podes sincronizar produtos.",
    shopify_col_title: "Título",
    shopify_col_vendor: "Fornecedor",
    shopify_col_type: "Tipo",
    shopify_col_status: "Estado",
    shopify_col_variants: "Variantes",
    shopify_col_updated: "Actualizado",
    shopify_new_tab_opened: "Aprovação Shopify aberta em novo separador. Completa lá e volta.",

    shop_kicker: "O catálogo",
    shop_title: "Peças que transformam uma casa em lar.",
    shop_subtitle: "Uma selecção discreta e cuidada de decoração, living e lifestyle — escolhida à mão na Suíça, enviada em todo o mundo, preço em CHF.",
    shop_catalog_title: "A Selecção",
    shop_pieces_curated: "peças curadas",
    shop_cat_all: "Tudo",
    shop_trust_secure: "Checkout seguro",
    shop_trust_shipping: "Envio mundial",
    shop_trust_curated: "Selecção manual",
    shop_view: "ver",
    shop_loading: "a carregar produtos…",
    shop_empty: "Ainda sem produtos — volta em breve.",
    shop_view: "ver",
    shop_back: "Voltar ao catálogo",
    shop_not_found: "Produto não encontrado.",
    shop_ai_enriched: "Enriquecido por IA",
    shop_buy_now: "Comprar agora",
    shop_processing: "A redireccionar…",
    shop_secure_checkout: "Checkout seguro via Stripe · CHF",
    shop_checkout_failed: "Não foi possível iniciar o checkout",
    shop_checkout_cancelled: "Checkout cancelado",
    shop_success_title: "Encomenda confirmada",
    shop_success_body: "Obrigado — o teu pagamento foi recebido. Vais receber um email de confirmação.",
    shop_success_pending: "Quase lá",
    shop_success_pending_body: "O pagamento está a ser processado. Podes fechar este separador com segurança.",
    shop_back_to_catalog: "Voltar ao catálogo",
    referral_prompt_title: "Partilha e ganha CHF 3 no próximo produto",
    referral_prompt_body: "Envia o teu link pessoal a um amigo. Sempre que alguém comprar através dele, creditamos CHF 3 na tua conta para a próxima encomenda em EMRKT.CH.",
    referral_prompt_email_placeholder: "O teu email",
    referral_prompt_generate: "Gerar o meu link",
    referral_prompt_copy: "Copiar link",
    referral_prompt_copied: "Link copiado — partilha à vontade!",
    referral_prompt_share: "Partilhar no WhatsApp",

    growth_title: "Studio de crescimento",
    growth_subtitle: "Publicações prontas para o Instagram, TikTok, Pinterest, WhatsApp, Facebook, X, LinkedIn e Reddit — em 5 línguas. Copia, cola nas tuas contas sociais, atrai tráfego orgânico grátis para a tua loja.",
    growth_bulk_generate: "Gerar para todos os produtos",
    growth_generating: "A gerar…",
    growth_generate_one: "Gerar este kit",
    growth_regenerate: "Gerar de novo",
    growth_products: "Produtos",
    growth_no_products: "Sincroniza o Shopify primeiro.",
    growth_pick_product: "Escolhe um produto à esquerda.",
    growth_not_generated: "Ainda sem kit — clica Gerar para escrever a primeira ronda de posts.",
    growth_no_content: "Sem conteúdo para este canal — gera de novo.",
    growth_copy: "Copiar",
    growth_copied: "Copiado — cola na tua rede social.",
    growth_kit_ready: "Kit pronto. Escolhe um canal e copia.",
    growth_tip: "Dica · Cola na tua conta, adiciona a imagem do produto (botão direito 'copiar imagem' na página do produto) e publica.",

    newsletter_title: "Recebe ofertas em CHF por email",
    newsletter_body: "Um email por semana com novidades e ofertas suíças. Sem spam, cancelas com um clique.",
    newsletter_email_placeholder: "o.teu@email.ch",
    newsletter_subscribe: "Subscrever",
    newsletter_subscribed_toast: "Subscrito — obrigado!",
    newsletter_subscribed_body: "Estás na lista — vemo-nos na tua caixa de entrada em breve.",
    newsletter_disclaimer: "RGPD / LCD suíça · cancela com um clique",

    ebooks_title: "Guias digitais",
    ebooks_subtitle: "Guias compactos, honestos e com referências de especialistas para os momentos que não cabem numa pesquisa Google. Entregues em PDF por email em segundos.",
    ebooks_available: "disponíveis",
    ebooks_none_yet: "Novos guias estão a ser escritos. Volta em breve.",
    ebooks_all: "Todos os guias",
    ebooks_email_placeholder: "o.teu@email.ch",
    ebooks_buy_now: "Comprar e descarregar",
    ebooks_digital_disclaimer: "Produto digital · entrega imediata · direito de retração renunciado no primeiro download (LCD suíça).",
    ebooks_success_title: "Pagamento recebido",
    ebooks_success_body: "O teu PDF está pronto e uma cópia foi enviada para o teu email.",
    ebooks_success_pending: "Quase lá",
    ebooks_success_pending_body: "A confirmar o pagamento — normalmente demora alguns segundos.",
    ebooks_download_now: "Descarregar PDF",
    ebooks_email_sent_to: "Também enviado para",
    ebooks_browse_more: "Ver mais guias",
    shop_ebooks_link: "Guias digitais",

    ebooks_admin_title: "Ebooks",
    ebooks_admin_subtitle: "O GPT-5.2 escreve guias de 4 000–5 500 palavras sobre temas parentais sensíveis com referências atribuídas a especialistas. 100% de margem.",
    ebooks_seed_title: "Geração com um clique",
    ebooks_seed_body: "Playbook de temas sensíveis — clica para começar a escrever um novo guia. Cada um leva ~2 minutos.",
    ebooks_generate: "Gerar",
    ebooks_admin_library: "Biblioteca",
    ebooks_admin_empty: "Sem ebooks ainda. Clica num tema semente para escrever o primeiro.",

    intel_product_title: "Anúncios de produto — gerados automaticamente",
    intel_pricing_title: "Preços sugeridos",
    intel_run: "Correr",
    intel_redo: "Correr de novo",
    intel_run_all: "Correr para todos",
    intel_running_all: "A correr…",
    intel_enriched: "Produto enriquecido",
    intel_priced: "Preço recomendado",
    intel_no_products: "Sincroniza a Shopify primeiro para veres produtos aqui.",
    intel_col_product: "Produto",
    intel_col_langs: "Idiomas",
    intel_col_tags: "Tags",
    intel_col_current: "Actual",
    intel_col_recommended: "Recomendado",
    intel_col_delta: "Δ",
    intel_col_confidence: "Confiança",
    intel_publish: "Publicar no Shopify",
    intel_publish_ok: "Traduções enviadas para o Shopify",
    intel_register_webhook: "Registar webhook de encomendas",
    intel_webhook_ok: "Webhook orders/create registado",

    auto_title: "Criar novos produtos",
    auto_subtitle: "Descreve uma ideia ou tendência. Construímos o produto completo — título, descrição em 5 idiomas, tags e preço — pronto para aprovares.",
    auto_seed_placeholder: "ex: canecas de viagem minimalistas em aço inox abaixo de CHF 40",
    auto_generate: "Gerar rascunho",
    auto_generating: "A gerar…",
    auto_generated_ok: "Rascunho criado",
    auto_published_ok: "Rascunho enviado para Shopify (status: draft)",
    auto_col_title: "Título",
    auto_col_type: "Tipo",
    auto_col_price: "Preço",
    auto_col_status: "Estado",
    auto_publish: "Enviar para Shopify",
    auto_reject: "Rejeitar",
    auto_view_shopify: "Abrir no Shopify",
    auto_empty: "Ainda não há rascunhos gerados pela IA.",
    auto_status_draft: "rascunho",
    auto_status_published: "publicado",
    auto_status_rejected: "rejeitado",

    support_title: "Conversas com clientes",
    support_only_escalated: "Só escaladas",
    support_escalated: "escalada",
    support_col_customer: "Cliente",
    support_col_last: "Última mensagem",
    support_col_msgs: "Msgs",
    support_col_when: "Atualizado",
    support_anonymous: "anónimo",
    support_empty: "Ainda sem conversas.",
    support_resolve: "Resolver",
    support_resolved: "Marcada como resolvida",
    support_status_resolved: "resolvida",

    inv_title: "Snapshot de inventário",
    inv_low_stock: "produtos com pouco stock",
    inv_threshold: "Limite",
    inv_col_product: "Produto",
    inv_col_stock: "Stock",
    inv_col_variants: "Variantes",
    inv_empty: "Sem dados de inventário — sincroniza a Shopify primeiro.",

    chat_title: "EMRKT.CH Support",
    chat_open: "Abrir chat",
    chat_send: "Enviar",
    chat_placeholder: "Pergunta sobre produtos, envio, devoluções…",
    chat_thinking: "a escrever…",
    chat_welcome: "Olá! Sou o assistente EMRKT.CH. Posso ajudar-te com produtos, envios e devoluções. Em que posso ajudar?",
    chat_error: "Desculpa, não consegui ligar ao servidor. Tenta novamente.",
    chat_escalated: "Encaminhada para um especialista humano",

    p4_trend_title: "Descobrir tendências",
    p4_market_title: "Tamanho do mercado",
    p4_seo_title: "Visibilidade no Google",
    p4_mkt_title: "Marketing e campanhas",
    p4_trend_ph: "ex: acessórios sustentáveis para animais",
    p4_market_ph: "ex: comedouros inteligentes para animais",
    p4_seo_ph: "handle do produto Shopify (ex: rotating-spice-rack)",
    p4_mkt_ph: "ex: Rotating Spice Rack",
    p4_run: "Analisar",
    p4_running: "A analisar…",
    p4_done: "Análise pronta",
    p4_history: "Análises recentes",
    p4_obj_awareness: "Notoriedade",
    p4_obj_conversion: "Conversão",
    p4_obj_retention: "Retenção",
    p4_pricing_country_title: "Preços por país (CH/DE/AT/FR/IT)",
    p4_pricing_compute: "Calcular",
    p4_pricing_recompute: "Recalcular",
    p4_pricing_done: "Preços por país calculados",

    login_admin_only: "Plataforma bloqueada · apenas admin durante o desenvolvimento",
    coming_soon_title: "Em desenvolvimento",
    coming_soon_body: "Esta plataforma encontra-se em desenvolvimento e ainda não está disponível ao público. Volta em breve.",
    coming_soon_home: "Voltar ao início",

    reviews_title: "Avaliações de clientes",
    reviews_count: "avaliações",
    reviews_verified: "compra verificada",
    reviews_verified_purchase: "verificada",
    reviews_more: "Ler mais",
    reviews_less: "Mostrar menos",
    reviews_admin_title: "Avaliações de produtos (reais, importadas)",
    reviews_admin_disclaimer: "Apenas reviews reais · lei suíça de transparência",
    reviews_product: "Produto",
    reviews_paste_json: "Colar reviews em JSON",
    reviews_import_json: "Importar",
    reviews_import_csv: "Carregar CSV",
    reviews_imported: "avaliações importadas",
    reviews_col_author: "Autor",
    reviews_col_rating: "Rating",
    reviews_col_body: "Texto",
    reviews_col_source: "Fonte",
    reviews_empty: "Sem avaliações — importa do AliExpress ou CSV.",
    reviews_judgeme_sync: "Sincronizar Judge.me",
    reviews_judgeme_syncing: "A sincronizar…",
    reviews_judgeme_synced: "avaliações sincronizadas do Judge.me",
    reviews_judgeme_not_configured: "Judge.me não configurado no backend",

    legal_title: "Páginas Legais e de Confiança",
    legal_status_empty: "Não criada",
    legal_status_draft: "Rascunho",
    legal_status_approved: "Aprovada",
    legal_status_published: "Publicada",
    legal_generate: "Gerar",
    legal_regenerate: "Regenerar",
    legal_approve: "Aprovar idioma atual",
    legal_generated: "Rascunho gerado",
    legal_approved: "Aprovada e agora pública",
    legal_publish_shopify: "Publicar no Shopify",
    legal_view_shopify: "Ver no Shopify",
    legal_published_shopify: "Publicada no Shopify",
    legal_edit_title: "Título",
    legal_edit_body: "Corpo (Markdown)",
    legal_status_published: "publicada",
    legal_last_updated: "Última atualização",
    legal_none: "Ainda não gerada.",
    legal_not_published: "Esta página ainda não foi publicada.",
    legal_pt_terms: "Termos de Venda",
    legal_pt_privacy: "Privacidade",
    legal_pt_dropshipping_disclosure: "Divulgação Dropshipping",
    legal_pt_refund: "Reembolsos & Retratação",
    legal_pt_shipping: "Envios",
    legal_pt_imprint: "Ficha da Empresa",

    footer_transparency_title: "Transparência total",
    footer_transparency_body: "A EMRKT.CH é uma plataforma suíça de dropshipping automatizado. Os produtos são enviados diretamente pelos nossos fornecedores parceiros. As despesas de envio e a responsabilidade do produto pertencem ao fornecedor — nós asseguramos a curadoria, o pagamento e o apoio ao cliente. Os teus direitos de consumidor (14 dias de retratação, garantia) mantêm-se totalmente protegidos.",
    footer_read_full: "Ler divulgação completa",

    designer_title: "Aspeto da loja e marca",
    designer_context: "Contexto de marca (opcional)",
    designer_context_ph: "ex: operado da Suíça, voz mediterrânica calorosa. Clientes 30-45 anos, urbanos, com sensibilidade estética.",
    designer_generate: "Gerar brief",
    designer_generating: "A desenhar…",
    designer_generated: "Brief pronto",
    designer_briefs: "Briefs recentes",
    designer_activate: "Ativar",
    designer_activated: "Brief ativado — loja atualizada",
    designer_active: "ativo",
    designer_view: "Ver",
    designer_none: "Gera o teu primeiro brief acima.",
  },
  de: {
    brand: "EMRKT.CH",
    tagline: "Die Zukunft des autonomen Handels",
    nav_login: "Anmelden",
    nav_get_started: "Loslegen",
    hero_kicker: "Phase 1 — Grundlage",
    hero_title: "Autonomer Handel.\nSchweizer Präzision.",
    hero_body: "EMRKT.CH orchestriert zwölf KI-Module, die Chancen entdecken, Produkte listen, dynamisch bepreisen, Kunden in fünf Sprachen bedienen und jede Entscheidung konform, protokolliert und erklärbar halten.",
    hero_cta_primary: "Dashboard öffnen",
    hero_cta_secondary: "Module ansehen",
    mission_kicker: "Mission",
    mission_title: "Den gesamten Handelszyklus automatisieren. Menschen behalten die Strategie.",
    vision_kicker: "Vision",
    vision_title: "Ein vertrauensorientierter, selbstverbessernder Handelsbetreiber — Ziel: CHF 1'000 Netto-Gewinn pro Tag.",
    principles_title: "Betriebsprinzipien",
    p1_title: "Vertrauen vor Gewinn",
    p1_body: "Kundenvertrauen ist das langfristige Kapital, nicht nur eine Werbeaussage.",
    p2_title: "Rechtssicher by design",
    p2_body: "Jede Entscheidung wird gegen lokales Recht und Plattformregeln geprüft.",
    p3_title: "Prüfbar & erklärbar",
    p3_body: "Unsichere, rechtliche oder strategische Entscheidungen werden eskaliert.",
    p4_title: "Nachhaltige Marge",
    p4_body: "Das Finance-Modul stellt Steuern zurück, bevor etwas Gewinn heisst.",
    modules_kicker: "Zwölf autonome Module",
    modules_title: "Eine CEO AI. Elf Spezialisten. Null Drift.",
    modules_body: "Die CEO AI koordiniert Entscheidungen über Trends, Preise, SEO, Marketing, Support, Finanzen und Compliance.",
    roadmap_title: "Roadmap",
    roadmap_p1: "Phase 1 — Grundlage (Infrastruktur, Auth, DB, CI/CD, Tests)",
    roadmap_p2: "Phase 2 — Commerce-Kern (Katalog, Checkout, Bestellungen)",
    roadmap_p3: "Phase 3 — Automatisierung (Listings, Übersetzung, Support)",
    roadmap_p4: "Phase 4 — Intelligenz (Trends, Preise, SEO, Kampagnen)",
    roadmap_p5: "Phase 5 — Kontinuierliches Lernen (Experimente, Selbstverbesserung)",
    footer_note: "EMRKT.CH · Für langfristiges Vertrauen gebaut.",
    login_title: "Anmelden",
    login_subtitle: "Zur EMRKT.CH Operator-Konsole.",
    login_email: "E-Mail",
    login_password: "Passwort",
    login_submit: "Anmelden",
    login_google: "Mit Google fortfahren",
    login_no_account: "Kein Konto?",
    login_register: "Erstellen",
    register_title: "Konto erstellen",
    register_name: "Vollständiger Name",
    register_submit: "Konto erstellen",
    register_have_account: "Konto vorhanden?",
    logging_in: "Anmeldung…",
    creating: "Wird erstellt…",
    sidebar_overview: "Übersicht",
    sidebar_modules: "KI-Module",
    sidebar_guardian: "Guardian AI",
    sidebar_finance: "Finanzen",
    sidebar_audit: "Audit-Log",
    sidebar_settings: "Einstellungen",
    sidebar_logout: "Abmelden",
    overview_title: "Operator-Konsole",
    overview_kpi_revenue: "Umsatz heute",
    overview_kpi_profit: "Nettogewinn heute",
    overview_kpi_orders: "Bestellungen heute",
    overview_kpi_trust: "Vertrauens-Score",
    overview_target: "Ziel · CHF 1'000/Tag",
    overview_modules_active: "Aktive Module",
    overview_guardian: "Guardian-Status",
    overview_briefing: "CEO AI Briefing",
    overview_briefing_hint: "Erstellt von GPT-5.2 in Ihrer Sprache.",
    overview_run_briefing: "Briefing aktualisieren",
    guardian_title: "Guardian AI — Systemgesundheit",
    guardian_subtitle: "Kontinuierliche Integrität von Infrastruktur, Integrationen und Compliance.",
    guardian_component: "Komponente",
    guardian_status: "Status",
    guardian_latency: "Latenz",
    guardian_message: "Meldung",
    audit_title: "Audit-Log",
    audit_subtitle: "Jede kritische Entscheidung hinterlässt eine Spur.",
    audit_ts: "Zeitstempel",
    audit_action: "Aktion",
    audit_actor: "Akteur",
    audit_meta: "Meta",
    finance_title: "Finanzübersicht",
    finance_subtitle: "Umsatz, Kosten, Steuerrückstellung, ausschüttbarer Gewinn.",
    finance_revenue: "Umsatz",
    finance_costs: "Kosten",
    finance_gross: "Bruttogewinn",
    finance_tax_reserve: "Steuerrückstellung (25%)",
    finance_distributable: "Ausschüttbarer Gewinn",
    finance_target: "Tagesziel",
    module_status_active: "aktiv",
    module_status_standby: "Standby",
    module_status_monitoring: "Monitor",
    module_status_training: "Training",
    module_briefing_title: "KI-Briefing",
    module_briefing_generate: "Briefing generieren",
    module_briefing_generating: "Wird generiert…",
    module_briefing_ask: "Modul fragen",
    module_briefing_placeholder: "Frage stellen oder leer lassen…",
    settings_title: "Einstellungen",
    settings_language: "Interfacesprache",
    settings_profile: "Profil",
  },
  fr: {
    brand: "EMRKT.CH",
    tagline: "Le futur du commerce autonome",
    nav_login: "Connexion",
    nav_get_started: "Commencer",
    hero_kicker: "Phase 1 — Fondation",
    hero_title: "Commerce autonome.\nPrécision suisse.",
    hero_body: "EMRKT.CH orchestre douze modules IA qui découvrent des opportunités, publient des produits, ajustent les prix, servent les clients en cinq langues et gardent chaque décision conforme, tracée et explicable.",
    hero_cta_primary: "Ouvrir le Dashboard",
    hero_cta_secondary: "Explorer les modules",
    mission_kicker: "Mission",
    mission_title: "Automatiser tout le cycle du commerce. Les humains gardent la stratégie.",
    vision_kicker: "Vision",
    vision_title: "Un opérateur commercial auto-amélioré, orienté confiance — cible CHF 1'000 de profit net par jour.",
    principles_title: "Principes opérationnels",
    p1_title: "La confiance avant le profit",
    p1_body: "La confiance client est notre actif composé, pas un slogan marketing.",
    p2_title: "Légal par design",
    p2_body: "Chaque décision est vérifiée contre le droit local et les règles des plateformes.",
    p3_title: "Auditable & explicable",
    p3_body: "Les décisions incertaines, juridiques ou stratégiques sont escaladées.",
    p4_title: "Marge durable",
    p4_body: "Le module Finance provisionne les impôts avant d'appeler quoi que ce soit du profit.",
    modules_kicker: "Douze modules autonomes",
    modules_title: "Une CEO AI. Onze spécialistes. Zéro dérive.",
    modules_body: "La CEO AI coordonne les décisions sur tendances, prix, SEO, marketing, support, finance et compliance.",
    roadmap_title: "Feuille de route",
    roadmap_p1: "Phase 1 — Fondation (infrastructure, auth, DB, CI/CD, tests)",
    roadmap_p2: "Phase 2 — Cœur commercial (catalogue, checkout, commandes)",
    roadmap_p3: "Phase 3 — Automatisation (listings, traduction, support)",
    roadmap_p4: "Phase 4 — Intelligence (tendances, prix, SEO, campagnes)",
    roadmap_p5: "Phase 5 — Apprentissage continu (expérimentation, auto-amélioration)",
    footer_note: "EMRKT.CH · Bâti pour la confiance de long terme.",
    login_title: "Se connecter",
    login_subtitle: "Console opérateur EMRKT.CH.",
    login_email: "Email",
    login_password: "Mot de passe",
    login_submit: "Se connecter",
    login_google: "Continuer avec Google",
    login_no_account: "Pas de compte ?",
    login_register: "Créer",
    register_title: "Créer un compte",
    register_name: "Nom complet",
    register_submit: "Créer le compte",
    register_have_account: "Déjà un compte ?",
    logging_in: "Connexion…",
    creating: "Création…",
    sidebar_overview: "Vue d'ensemble",
    sidebar_modules: "Modules IA",
    sidebar_guardian: "Guardian AI",
    sidebar_finance: "Finances",
    sidebar_audit: "Journal d'audit",
    sidebar_settings: "Paramètres",
    sidebar_logout: "Se déconnecter",
    overview_title: "Console opérateur",
    overview_kpi_revenue: "Recettes aujourd'hui",
    overview_kpi_profit: "Profit net aujourd'hui",
    overview_kpi_orders: "Commandes aujourd'hui",
    overview_kpi_trust: "Score de confiance",
    overview_target: "Cible · CHF 1'000/jour",
    overview_modules_active: "Modules actifs",
    overview_guardian: "État Guardian",
    overview_briefing: "Briefing CEO AI",
    overview_briefing_hint: "Généré par GPT-5.2 dans votre langue.",
    overview_run_briefing: "Rafraîchir le briefing",
    guardian_title: "Guardian AI — Santé du système",
    guardian_subtitle: "Intégrité continue de l'infrastructure, des intégrations et de la compliance.",
    guardian_component: "Composant",
    guardian_status: "État",
    guardian_latency: "Latence",
    guardian_message: "Message",
    audit_title: "Journal d'audit",
    audit_subtitle: "Chaque décision critique laisse une trace.",
    audit_ts: "Horodatage",
    audit_action: "Action",
    audit_actor: "Acteur",
    audit_meta: "Meta",
    finance_title: "Vue financière",
    finance_subtitle: "Recettes, coûts, provision fiscale, profit distribuable.",
    finance_revenue: "Recettes",
    finance_costs: "Coûts",
    finance_gross: "Profit brut",
    finance_tax_reserve: "Provision fiscale (25%)",
    finance_distributable: "Profit distribuable",
    finance_target: "Cible journalière",
    module_status_active: "actif",
    module_status_standby: "veille",
    module_status_monitoring: "surveille",
    module_status_training: "entraînement",
    module_briefing_title: "Briefing IA",
    module_briefing_generate: "Générer le briefing",
    module_briefing_generating: "Génération…",
    module_briefing_ask: "Interroger le module",
    module_briefing_placeholder: "Posez une question ou laissez vide…",
    settings_title: "Paramètres",
    settings_language: "Langue de l'interface",
    settings_profile: "Profil",
  },
  it: {
    brand: "EMRKT.CH",
    tagline: "Il Futuro del Commercio Autonomo",
    nav_login: "Accedi",
    nav_get_started: "Inizia",
    hero_kicker: "Fase 1 — Fondazione",
    hero_title: "Commercio autonomo.\nPrecisione svizzera.",
    hero_body: "EMRKT.CH orchestra dodici moduli IA che scoprono opportunità, pubblicano prodotti, adeguano i prezzi dinamicamente, servono i clienti in cinque lingue e mantengono ogni decisione conforme, tracciata ed spiegabile.",
    hero_cta_primary: "Apri il Dashboard",
    hero_cta_secondary: "Esplora i moduli",
    mission_kicker: "Missione",
    mission_title: "Automatizzare l'intero ciclo di commercio. Gli umani mantengono la strategia.",
    vision_kicker: "Visione",
    vision_title: "Un operatore commerciale auto-migliorante orientato alla fiducia — obiettivo CHF 1.000 di profitto netto al giorno.",
    principles_title: "Principi operativi",
    p1_title: "Fiducia prima del profitto",
    p1_body: "La fiducia del cliente è l'asset composto, non uno slogan di marketing.",
    p2_title: "Legale by design",
    p2_body: "Ogni decisione è verificata contro leggi locali e regole delle piattaforme.",
    p3_title: "Verificabile & spiegabile",
    p3_body: "Decisioni incerte, legali o strategiche vengono escalate con traccia completa.",
    p4_title: "Margine sostenibile",
    p4_body: "Il modulo Finance accantona le tasse prima di chiamare qualcosa profitto.",
    modules_kicker: "Dodici moduli autonomi",
    modules_title: "Una CEO AI. Undici specialisti. Zero deriva.",
    modules_body: "La CEO AI coordina decisioni su tendenze, prezzi, SEO, marketing, supporto, finanza e compliance.",
    roadmap_title: "Roadmap",
    roadmap_p1: "Fase 1 — Fondazione (infrastruttura, auth, DB, CI/CD, test)",
    roadmap_p2: "Fase 2 — Nucleo commerciale (catalogo, checkout, ordini)",
    roadmap_p3: "Fase 3 — Automazione (listing, traduzioni, supporto)",
    roadmap_p4: "Fase 4 — Intelligenza (tendenze, prezzi, SEO, campagne)",
    roadmap_p5: "Fase 5 — Apprendimento continuo (esperimenti, auto-miglioramento)",
    footer_note: "EMRKT.CH · Costruito per fiducia a lungo termine.",
    login_title: "Accedi",
    login_subtitle: "Console operatore EMRKT.CH.",
    login_email: "Email",
    login_password: "Password",
    login_submit: "Accedi",
    login_google: "Continua con Google",
    login_no_account: "Nessun account?",
    login_register: "Crea",
    register_title: "Crea account",
    register_name: "Nome completo",
    register_submit: "Crea account",
    register_have_account: "Hai già un account?",
    logging_in: "Accesso…",
    creating: "Creazione…",
    sidebar_overview: "Panoramica",
    sidebar_modules: "Moduli IA",
    sidebar_guardian: "Guardian AI",
    sidebar_finance: "Finanze",
    sidebar_audit: "Log audit",
    sidebar_settings: "Impostazioni",
    sidebar_logout: "Esci",
    overview_title: "Console operatore",
    overview_kpi_revenue: "Ricavi oggi",
    overview_kpi_profit: "Profitto netto oggi",
    overview_kpi_orders: "Ordini oggi",
    overview_kpi_trust: "Punteggio fiducia",
    overview_target: "Obiettivo · CHF 1.000/giorno",
    overview_modules_active: "Moduli attivi",
    overview_guardian: "Stato Guardian",
    overview_briefing: "Briefing CEO AI",
    overview_briefing_hint: "Generato da GPT-5.2 nella tua lingua.",
    overview_run_briefing: "Aggiorna briefing",
    guardian_title: "Guardian AI — Salute del sistema",
    guardian_subtitle: "Integrità continua di infrastruttura, integrazioni e compliance.",
    guardian_component: "Componente",
    guardian_status: "Stato",
    guardian_latency: "Latenza",
    guardian_message: "Messaggio",
    audit_title: "Log audit",
    audit_subtitle: "Ogni decisione critica lascia una traccia.",
    audit_ts: "Timestamp",
    audit_action: "Azione",
    audit_actor: "Attore",
    audit_meta: "Meta",
    finance_title: "Vista finanziaria",
    finance_subtitle: "Ricavi, costi, riserva fiscale, profitto distribuibile.",
    finance_revenue: "Ricavi",
    finance_costs: "Costi",
    finance_gross: "Profitto lordo",
    finance_tax_reserve: "Riserva fiscale (25%)",
    finance_distributable: "Profitto distribuibile",
    finance_target: "Obiettivo giornaliero",
    module_status_active: "attivo",
    module_status_standby: "standby",
    module_status_monitoring: "monitor",
    module_status_training: "training",
    module_briefing_title: "Briefing IA",
    module_briefing_generate: "Genera briefing",
    module_briefing_generating: "Generazione…",
    module_briefing_ask: "Chiedi al modulo",
    module_briefing_placeholder: "Fai una domanda o lascia vuoto…",
    settings_title: "Impostazioni",
    settings_language: "Lingua dell'interfaccia",
    settings_profile: "Profilo",
  },
};

export const LANGS = [
  { code: "pt", label: "Português" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
];

export function t(lang, key) {
  const dict = DICT[lang] || DICT.en;
  return dict[key] ?? DICT.en[key] ?? key;
}
